import type { Theme } from '@mend/mui';

import { axisClasses } from '@mui/x-charts-pro';
import { addDays, format } from 'date-fns';

export const WIDGET_CHART_HEIGHT = 300;
export const STAT_WIDGET_CHART_HEIGHT = 110;
export const CHART_SERIES_COLORS = {
  dark: [
    '#f9d780',
    '#c838f6',
    '#ff6b9d',
    '#1e88e5',
    '#26c6da',
    '#4caf50',
    '#81c784',
    '#ff9800',
    '#ff7043',
    '#2e7d32',
    '#d32f2f',
    '#9c27b0',
    '#795548',
    '#3f51b5',
    '#9e9e9e',
    '#ffc107',
    '#607d8b',
    '#673ab7',
    '#1565c0',
    '#57d489',
    '#ff5757',
  ],
  light: [
    '#d97706',
    '#7c3aed',
    '#de7095',
    '#1565c0',
    '#0097a7',
    '#2e7d32',
    '#4acbad',
    '#f57c00',
    '#d84315',
    '#1b5e20',
    '#b71c1c',
    '#7b1fa2',
    '#795548',
    '#283593',
    '#616161',
    '#ff8f00',
    '#455261',
    '#4a148c',
    '#0d47a1',
    '#15803d',
    '#dc2626',
  ],
};

export const CHART_AXIS_STYLES = (theme: Theme) => ({
  [`.${axisClasses.label}`]: {
    fill: theme.palette.muted[4],
  },
  [`.${axisClasses.line}`]: {
    stroke: theme.palette.muted[4],
  },
  [`.${axisClasses.tick}`]: {
    stroke: theme.palette.muted[4],
  },
  [`.${axisClasses.tickLabel}`]: {
    fill: theme.palette.muted[4],
  },
});

/**
 * X-Axis configurations for charts whose values are dates and are
 * dynamically grouped by month, week, day, or hour.
 */
export const CHART_X_AXIS_CONFIG = {
  month: {
    tickLabelInterval: 'auto',
    tickMinStep: 2_592_000_000, // 30 days in milliseconds
    valueFormatter: (
      date: Date,
      context: {
        location?: 'tooltip' | 'tick' | 'legend' | 'zoom-slider-tooltip';
      }
    ) => {
      const formatString =
        context.location === 'tooltip' ? `MMMM yyyy` : `MMM yy`;
      return format(date, formatString);
    },
  },
  week: {
    tickLabelInterval: 'auto',
    tickMinStep: 604_800_000, // 1 week in milliseconds
    valueFormatter: (
      date: Date,
      context: {
        location?: 'tooltip' | 'tick' | 'legend' | 'zoom-slider-tooltip';
      }
    ) => {
      if (context.location === 'tooltip') {
        const endDate = addDays(date, 6);
        const formatString = `MMMM do, yyyy`;
        return `${format(date, formatString)} - ${format(endDate, formatString)}`;
      }
      return format(date, `M/d`);
    },
  },
  day: {
    tickLabelInterval: 'auto',
    tickMinStep: 86_400_000, // 1 day in milliseconds
    valueFormatter: (
      date: Date,
      context: {
        location?: 'tooltip' | 'tick' | 'legend' | 'zoom-slider-tooltip';
      }
    ) => {
      const formatString =
        context.location === 'tooltip' ? `EEEE, MMMM do` : `E'\n'M/d`;
      return format(date, formatString);
    },
  },
  hour: {
    tickLabelInterval: 'auto',
    tickMinStep: 3_600_000, // 1 hour in milliseconds
    valueFormatter: (
      date: Date,
      context: {
        location?: 'tooltip' | 'tick' | 'legend' | 'zoom-slider-tooltip';
      }
    ) => {
      const formatString =
        context.location === 'tooltip' ? `h aaa, MMMM do` : `haaa`;
      return format(date, formatString);
    },
  },
} as const;
